import { useState, useRef } from "react";
import html2canvas from "html2canvas";

// -------------------------------------------------------------------
// Utility
// -------------------------------------------------------------------
const now = () => {
  const d = new Date();
  return {
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
  };
};

// -------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------

function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, color: "#6b7280" }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 10, color: "#374151" }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  background: "#1a1d23",
  border: "1px solid #1f2128",
  borderRadius: 6,
  height: 32,
  padding: "0 10px",
  fontSize: 12,
  color: "#e5e7eb",
  width: "100%",
  outline: "none",
};

function Input({ id, ...props }) {
  return <input style={inputStyle} {...props} />;
}

function Select({ children, ...props }) {
  return (
    <select style={{ ...inputStyle, appearance: "auto" }} {...props}>
      {children}
    </select>
  );
}

// -------------------------------------------------------------------
// Trade card (also used for the dimmed sample)
// -------------------------------------------------------------------
function TradeCard({ trade, sample = false }) {
  const isWin = trade.pnl >= 0;
  const sign = trade.pnl >= 0 ? "+" : "";
  const ptSign = trade.rawPts >= 0 ? "+" : "";

  return (
    <div
      style={{
        background: "#111214",
        borderRadius: 10,
        padding: 16,
        border: sample ? "1px solid #1a1d23" : "1px solid #1a4731",
        opacity: sample ? 0.38 : 1,
        filter: sample ? "blur(0.4px)" : "none",
        pointerEvents: sample ? "none" : "auto",
        transition: "all 0.3s",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Trade result
        </span>
        {trade.tag && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 20,
              background: "#1a2d3a",
              color: "#60b8d4",
              maxWidth: 140,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {trade.tag.toUpperCase()}
          </span>
        )}
      </div>

      {/* Instrument */}
      <div style={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>{trade.inst || "—"}</div>
      <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 10 }}>
        {trade.date} · {trade.time}
      </div>

      {/* Direction */}
      <span
        style={{
          display: "inline-block",
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 4,
          marginBottom: 10,
          background: trade.dir === "LONG" ? "#0d2b1f" : "#2b0d0d",
          color: trade.dir === "LONG" ? "#34d399" : "#f87171",
        }}
      >
        {trade.dir}
      </span>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        {[
          { label: "Entry", value: trade.entry },
          { label: "Exit", value: trade.exit },
          { label: "Qty", value: trade.qty ? `${trade.qty}x` : "—" },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: "#1a1d23", borderRadius: 6, padding: "7px 8px" }}>
            <div style={{ fontSize: 9, color: "#6b7280" }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#e5e7eb" }}>{value ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* P&L */}
      <div
        style={{
          background: isWin ? "#0d2b1f" : "#2b0d0d",
          borderRadius: 8,
          padding: "10px 12px",
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 24, fontWeight: 700, color: isWin ? "#34d399" : "#f87171" }}>
          {sign}${Math.abs(trade.pnl).toFixed(2)}
        </span>
        <span style={{ fontSize: 12, color: isWin ? "#34d399" : "#f87171", opacity: 0.8, marginLeft: 6 }}>
          {ptSign}{trade.rawPts.toFixed(2)} pts
        </span>
      </div>

      {/* Note */}
      {trade.note && (
        <div style={{ fontSize: 10, color: "#6b7280", fontStyle: "italic", marginBottom: 10 }}>
          "{trade.note}"
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #1f2128",
          paddingTop: 8,
        }}
      >
        <span style={{ fontSize: 9, color: "#374151" }}>futures-os.app</span>
        {trade.wr != null && !isNaN(trade.wr) && (
          <span style={{ fontSize: 10, fontWeight: 600, color: "#a3e635" }}>
            W% {trade.wr.toFixed(1)}
          </span>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// CTA Banner
// -------------------------------------------------------------------
function CtaBanner() {
  return (
    <div
      style={{
        background: "#0d2b1f",
        border: "1px solid #1a4731",
        borderRadius: 10,
        padding: "16px 20px",
        marginTop: 20,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#a3e635", marginBottom: 3 }}>
          Want to know what's actually driving your win rate?{" "}
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              background: "#34d399",
              color: "#0d2b1f",
              borderRadius: 20,
              padding: "2px 8px",
              marginLeft: 4,
            }}
          >
            30 days free
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#2d6a49" }}>
          Futures OS analyzes your behavior patterns across all your prop accounts.
        </div>
      </div>
      <a
        href="https://futures-os.app"
        style={{
          background: "#4caf7d",
          color: "#0d2b1f",
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 6,
          padding: "8px 16px",
          whiteSpace: "nowrap",
          textDecoration: "none",
          flexShrink: 0,
        }}
      >
        Start free trial
      </a>
    </div>
  );
}

// -------------------------------------------------------------------
// Main component
// -------------------------------------------------------------------
export function TradeCardGenerator() {
  const [form, setForm] = useState({
    inst: "",
    dir: "LONG",
    entry: "",
    exit: "",
    entryTime: "",
    exitTime: "",
    qty: "1",
    wr: "",
    tag: "",
    note: "",
  });
  const [generated, setGenerated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copyState, setCopyState] = useState(null);
  const cardRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const buildTrade = () => {
    const entry = parseFloat(form.entry) || 0;
    const exit = parseFloat(form.exit) || 0;
    const qty = parseInt(form.qty) || 1;
    const rawPts = form.dir === "LONG" ? exit - entry : entry - exit;
    const pnl = rawPts * qty;
    const { date } = now();
    const entryTime = form.entryTime.trim();
    const exitTime = form.exitTime.trim();
    const timeStr = entryTime && exitTime ? `${entryTime} – ${exitTime}` : entryTime || exitTime || now().time;
    return {
      inst: form.inst.trim().toUpperCase(),
      dir: form.dir,
      entry,
      exit,
      qty,
      rawPts,
      pnl,
      tag: form.tag.trim() || "Trade",
      note: form.note.trim(),
      wr: form.wr !== "" ? parseFloat(form.wr) : null,
      date,
      time: timeStr,
    };
  };

  const generate = () => {
    if (!form.inst.trim()) return;
    setLoading(true);
    setTimeout(() => {
      setGenerated(buildTrade());
      setLoading(false);
    }, 650);
  };

  const screenshotCard = async () => {
    if (!cardRef.current) return;
    setCopyState("screenshot");
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#111214", scale: 2 });
      canvas.toBlob((blob) => {
        const item = new ClipboardItem({ "image/png": blob });
        navigator.clipboard.write([item]).then(() => {
          setTimeout(() => setCopyState(null), 2200);
        });
      });
    } catch (e) {
      setCopyState(null);
    }
  };

  const copyDiscordText = () => {
    const t = generated;
    if (!t) return;
    const sign = t.pnl >= 0 ? "+" : "";
    const ptSign = t.rawPts >= 0 ? "+" : "";
    const text = [
      `**${t.inst} ${t.dir}** · ${t.tag}`,
      `Entry: ${t.entry} → Exit: ${t.exit} (${ptSign}${t.rawPts.toFixed(2)} pts)`,
      `Qty: ${t.qty} · P&L: ${sign}$${Math.abs(t.pnl).toFixed(2)}${t.wr != null ? ` · W%: ${t.wr.toFixed(1)}` : ""}`,
      t.note ? `"${t.note}"` : null,
      "futures-os.app",
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard.writeText(text).then(() => {
      setCopyState("text");
      setTimeout(() => setCopyState(null), 2200);
    });
  };

  const SAMPLE_TRADE = {
    inst: "—", dir: "LONG", entry: "—", exit: "—", qty: null,
    rawPts: 0, pnl: 0, tag: "Your setup", note: "",
    wr: null, date: "Jul 15", time: "2:41 PM",
  };

  return (
    <div style={{ background: "#0d0f12", minHeight: "100vh", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#e5e7eb" }}>
      {/* Nav */}
      <nav
        style={{
          background: "#111317",
          borderBottom: "1px solid #1f2128",
          padding: "0 24px",
          height: 52,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <a href="/" style={{ fontSize: 15, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em", textDecoration: "none" }}>
          Futures<span style={{ color: "#4caf7d" }}>OS</span>
        </a>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          {["Dashboard", "Analytics"].map((l) => (
            <a key={l} href="/" style={{ fontSize: 12, color: "#6b7280", textDecoration: "none" }}>{l}</a>
          ))}
          <span style={{ fontSize: 12, color: "#4caf7d", fontWeight: 600 }}>Trade card</span>
          <a href="/pricing" style={{ fontSize: 12, color: "#6b7280", textDecoration: "none" }}>Pricing</a>
          <a
            href="/signup"
            style={{
              fontSize: 12, fontWeight: 600, background: "#4caf7d", color: "#0d2b1f",
              padding: "5px 14px", borderRadius: 6, textDecoration: "none",
            }}
          >
            Start free trial
          </a>
        </div>
      </nav>

      {/* Body */}
      <div style={{ padding: "28px 24px", maxWidth: 860, margin: "0 auto" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#4caf7d", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
          Free tool
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em", marginBottom: 6 }}>
          Trade card generator
        </h1>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>
          Build a shareable trade card for Discord, X, or anywhere you post your results.
        </p>

        {/* Two-column layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>
          {/* Form */}
          <div style={{ background: "#111317", border: "1px solid #1f2128", borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "#4b5563", textTransform: "uppercase", marginBottom: 10 }}>
              Trade details
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <Field label="Instrument">
                <Input
                  type="text"
                  placeholder="MNQ, AAPL, BTC…"
                  maxLength={12}
                  value={form.inst}
                  onChange={set("inst")}
                />
              </Field>
              <Field label="Direction">
                <Select value={form.dir} onChange={set("dir")}>
                  <option>LONG</option>
                  <option>SHORT</option>
                </Select>
              </Field>
              <Field label="Entry">
                <Input type="number" placeholder="0.00" value={form.entry} onChange={set("entry")} />
              </Field>
              <Field label="Exit">
                <Input type="number" placeholder="0.00" value={form.exit} onChange={set("exit")} />
              </Field>
              <Field label="Entry time">
                <Input type="text" placeholder="15:55:40" value={form.entryTime} onChange={set("entryTime")} />
              </Field>
              <Field label="Exit time">
                <Input type="text" placeholder="15:55:44" value={form.exitTime} onChange={set("exitTime")} />
              </Field>
              <Field label="Contracts / shares">
                <Input type="number" min="1" value={form.qty} onChange={set("qty")} />
              </Field>
              <Field label="Win rate % (optional)">
                <Input type="number" placeholder="75.0" step="0.01" value={form.wr} onChange={set("wr")} />
              </Field>
            </div>

            <div style={{ marginTop: 10 }}>
              <Field label="Setup tag" hint="Shows as a badge on your card — use whatever your system calls it.">
                <Input
                  type="text"
                  placeholder="e.g. VWAP bounce, breakout, reversal…"
                  value={form.tag}
                  onChange={set("tag")}
                />
              </Field>
            </div>

            <div style={{ marginTop: 8 }}>
              <Field label="Note (optional)">
                <Input
                  type="text"
                  placeholder="Clean entry, held through noise…"
                  value={form.note}
                  onChange={set("note")}
                />
              </Field>
            </div>

            <button
              onClick={generate}
              disabled={loading || !form.inst.trim()}
              style={{
                width: "100%",
                marginTop: 14,
                background: loading ? "#1a4731" : !form.inst.trim() ? "#1a2a1f" : "#4caf7d",
                color: loading ? "#4caf7d" : !form.inst.trim() ? "#2d5a3d" : "#0d2b1f",
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 8,
                border: "none",
                padding: 10,
                cursor: form.inst.trim() && !loading ? "pointer" : "not-allowed",
                transition: "all .15s",
              }}
            >
              {loading ? "Generating…" : "Generate card"}
            </button>
          </div>

          {/* Card panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ position: "relative" }} ref={cardRef}>
              <TradeCard trade={generated ?? SAMPLE_TRADE} sample={!generated} />
              {!generated && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                  }}
                >
                  <div
                    style={{
                      background: "#1a1d23",
                      border: "1px solid #2a2d35",
                      borderRadius: 20,
                      padding: "5px 14px",
                      fontSize: 11,
                      color: "#4b5563",
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                    }}
                  >
                    Your card appears here
                  </div>
                </div>
              )}
            </div>

            {generated && (
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { key: "screenshot", label: copyState === "screenshot" ? "Copied!" : "Copy for Discord", primary: true },
                  { key: "text", label: copyState === "text" ? "Copied!" : "Text only" },
                ].map(({ key, label, primary }) => (
                  <button
                    key={key}
                    onClick={key === "screenshot" ? screenshotCard : copyDiscordText}
                    style={{
                      flex: 1,
                      background: copyState === key
                        ? (key === "text" ? "#0d2b1f" : "#1a1d23")
                        : primary ? "#1e2d4a" : "#1a1d23",
                      border: `1px solid ${copyState === key ? (key === "text" ? "#4caf7d" : "#374151") : primary ? "#5b9cf6" : "#1f2128"}`,
                      borderRadius: 6,
                      padding: 7,
                      fontSize: 11,
                      color: copyState === key
                        ? (key === "text" ? "#4caf7d" : "#a3e635")
                        : primary ? "#5b9cf6" : "#9ca3af",
                      fontWeight: primary ? 600 : 400,
                      cursor: "pointer",
                      transition: "all .15s",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {generated && <CtaBanner />}
      </div>
    </div>
  );
}
